import { useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { NotificationRuleFormData } from '@/features/notification-rules/schemas/create-notification-rule.schema.ts';
import { CaretSortIcon } from '@radix-ui/react-icons';
import {
  DEVICE_TYPE_CODE_BY_NAME,
  PLANT_MEASUREMENT_CODE_BY_NAME,
  COMMON_DEVICE_FIELDS,
} from '@/features/devices/schemas/devices.schemas.ts';
import {
  DEVICE_EVENTS_METADATA,
  PLANT_FIELDS_METADATA,
} from '@/features/devices/schemas/devices.metadata.ts';
import { Button } from '@/components/ui/button.tsx';
import { Check, Plus, Trash2 } from 'lucide-react';
import { StatusAlert } from '@/features/notification-rules/components/create-notification-rule/status-alert.tsx';
import { cn } from '@/utils/utils.ts';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx';
import { Input } from '@/components/ui/input.tsx';
import {
  getAvailableParameters,
  getParameterLabel,
  OPERATORS,
} from '@/features/notification-rules/schemas/notification-rules.utils.ts';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';

export function CreateNotificationRuleConditionsForm() {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const form = useFormContext<NotificationRuleFormData>();

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'conditions',
  });

  const addCondition = () => {
    if (fields.length >= 3) {
      return setErrorMessage('You cannot add more than 3 conditions');
    }

    return append({ fact: '', operator: 'equal', value: '' });
  };

  const removeCondition = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  useEffect(() => {
    setErrorMessage(undefined);
  }, []);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">Conditions</h2>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addCondition}
          className="flex items-center gap-2"
        >
          <Plus size={16} />
          Add Condition
        </Button>
      </div>

      {errorMessage && <StatusAlert type="error" message={errorMessage} />}

      {fields.map((field, index) => (
        <div className="group flex flex-col" key={field.id}>
          <div
            className={cn(
              'grid grid-cols-2 flex-wrap items-start gap-2 gap-y-0',
              fields.length > 1
                ? 'sm:grid-cols-[repeat(3,minmax(170px,_1fr))_40px]'
                : 'sm:grid-cols-3'
            )}
          >
            <div className="flex-2">
              <ParameterComboboxSelect index={index} />
            </div>
            <div className="flex-2">
              <FormField
                control={form.control}
                name={`conditions.${index}.operator`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="required">Condition</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {OPERATORS.map((op) => (
                          <SelectItem key={String(op.value)} value={op.value}>
                            {op.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="">
              <ConditionValueBasedOnParameter index={index} />
            </div>

            {fields.length > 1 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => removeCondition(index)}
                className="mt-6 flex h-9 items-center gap-1 justify-self-start text-red-600 hover:bg-red-50 hover:text-red-700"
              >
                <Trash2 size={16} />
              </Button>
            )}
          </div>

          <div className="relative h-4 group-last:hidden">
            <div className="absolute top-2 w-full border-b"></div>
            <div className="absolute -top-1.5 left-3/6 -translate-x-2/4">
              <span className="text-muted-foreground bg-white px-2 text-xs">AND</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ParameterComboboxSelect({ index }: { index: number }) {
  const [open, setOpen] = useState(false);

  const form = useFormContext<NotificationRuleFormData>();
  const watchRuleType = form.watch('ruleType');
  const watchDeviceType = form.watch('deviceType');
  const watchConditionFact = form.watch(`conditions.${index}.fact`);

  const availableParameters = getAvailableParameters({
    watchRuleType,
    watchDeviceType,
  });

  const parameterLabel = getParameterLabel({
    watchDeviceType,
    watchRuleType,
    fieldValue: watchConditionFact,
  });

  return (
    <FormField
      control={form.control}
      name={`conditions.${index}.fact`}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="required">Parameter</FormLabel>
          <Popover open={open} onOpenChange={setOpen} modal={true}>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className="w-full justify-between px-3 py-2 text-sm font-normal"
                >
                  <div className="overflow-hidden">
                    {field.value ? (
                      parameterLabel
                    ) : (
                      <span className="text-muted-foreground">Select parameter</span>
                    )}
                  </div>
                  <CaretSortIcon className="min-h-4 min-w-4 opacity-50" />
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent className="p-0" align="start">
              <Command>
                <CommandInput placeholder="Search parameter" className="h-9" />
                <CommandList>
                  <CommandEmpty>No parameter found.</CommandEmpty>
                  <CommandGroup>
                    {availableParameters?.map((param) => (
                      <CommandItem
                        key={param.id}
                        value={param.id}
                        onSelect={() => {
                          field.onChange(param.id === field.value ? '' : String(param.id));
                          setOpen(false);
                        }}
                      >
                        {param.name} {param.unit && `(${param.unit})`}
                        <Check
                          className={cn(
                            'ml-auto',
                            field.value === param.id ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function ConditionValueBasedOnParameter({ index }: { index: number }) {
  const isInitialRender = useRef(true);

  const form = useFormContext<NotificationRuleFormData>();

  const watchRuleType = form.watch('ruleType');
  const watchDeviceType = form.watch('deviceType');
  const watchConditionParameter = Number(form.watch(`conditions.${index}.fact`));

  const isValueFieldDisabled = Boolean(!watchConditionParameter);

  useEffect(() => {
    // skip initial render to avoid clearing user input during component mount
    if (!isInitialRender.current) {
      form.setValue(`conditions.${index}.value`, '');
    }

    isInitialRender.current = false;

    return () => {
      isInitialRender.current = true;
    };
  }, [watchConditionParameter]);

  if (watchRuleType === 'device' && watchDeviceType) {
    if (watchConditionParameter === COMMON_DEVICE_FIELDS['Event Status']) {
      let OPTIONS: {
        id: string | number;
        name: string;
      }[] = [];

      if (watchDeviceType) {
        const deviceTypeId = DEVICE_TYPE_CODE_BY_NAME[watchDeviceType];
        const eventDeviceMetadata = DEVICE_EVENTS_METADATA[deviceTypeId];

        OPTIONS = Object.values(eventDeviceMetadata.parametersByCode)
          .map((param) => ({
            id: param?.id,
            name: param?.name,
          }))
          .sort((a, b) => a?.name?.localeCompare(b?.name));
      }

      return (
        <FormField
          control={form.control}
          name={`conditions.${index}.value`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="required">Value</FormLabel>
              <Select
                onValueChange={field.onChange}
                defaultValue={field.value}
                disabled={isValueFieldDisabled}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select value" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {OPTIONS?.map((op) => (
                    <SelectItem key={String(op.id)} value={String(op.id)}>
                      {op.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }

    return (
      <FormField
        control={form.control}
        name={`conditions.${index}.value`}
        render={({ field }) => (
          <FormItem className="mb-2">
            <FormLabel className="required">Value</FormLabel>
            <FormControl>
              <Input
                type="number"
                placeholder="Enter value"
                {...field}
                disabled={isValueFieldDisabled}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  if (watchRuleType === 'plant') {
    let OPTIONS: {
      id: string | number;
      name: string;
    }[] = [];

    if (watchConditionParameter === PLANT_MEASUREMENT_CODE_BY_NAME['MQTT Connection Status']) {
      const parameterId = PLANT_MEASUREMENT_CODE_BY_NAME['MQTT Connection Status'];

      OPTIONS = Object.values(PLANT_FIELDS_METADATA[parameterId].parametersByCode)
        .map((param) => ({
          id: param?.id,
          name: param?.name,
        }))
        .sort((a, b) => a?.name?.localeCompare(b?.name));
    }

    return (
      <FormField
        control={form.control}
        name={`conditions.${index}.value`}
        render={({ field }) => (
          <FormItem>
            <FormLabel className="required">Value</FormLabel>
            <Select
              onValueChange={field.onChange}
              defaultValue={field.value}
              disabled={isValueFieldDisabled}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select value" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {OPTIONS?.map((op) => (
                  <SelectItem key={String(op.id)} value={String(op.id)}>
                    {op.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  return (
    <FormField
      control={form.control}
      name={`conditions.${index}.value`}
      render={({ field }) => (
        <FormItem className="mb-2">
          <FormLabel className="required">Value</FormLabel>
          <FormControl>
            <Input type="number" placeholder="Enter value" {...field} disabled={true} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
